import { successAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { mergeSectionValues } from './mergeSectionValues';

export interface SectionSaveOptions<Dto, Form extends object, Request, Section extends string> {
    section: Section;
    /** The form fields each section owns; `custom-fields` owns every custom field. */
    sectionFields: Record<Section, readonly string[]>;
    /**
     * The record as stored now, such as `() => refetch().unwrap()` on the page's by-id query. It runs before the
     * PUT, which is built on it, and again after, so the read view shows what was saved.
     */
    fetchLatest: () => Promise<Dto>;
    toForm: (dto: Dto) => Form;
    /**
     * The request for the merged form, with the record it was merged over, as a form and as fetched; `null` refuses
     * the save and keeps the section open, once a field says why or the user declined a confirmation it awaited.
     */
    toRequest: (edited: Form, latest: Form, latestDto: Dto) => Request | null | Promise<Request | null>;
    /** The PUT, unwrapped, so its rejection reaches the section as the section's error. */
    update: (request: Request) => Promise<unknown>;
    /** Toasted once saved, for an update endpoint the API layer does not toast itself. */
    successMessageId?: string;
    onSaved: () => void;
}

let isSending = false;

/**
 * Whether a section's save is starting one of its two requests at this moment: the read of the record, or the PUT.
 * The section shows that failure itself, so the API error handler raises no toast for a request started meanwhile.
 * RTK Query announces a request before the call that starts it returns, so nothing else starts in between, and a
 * request that fails while the save waits keeps its toast.
 */
export const isSendingSectionRequest = (): boolean => isSending;

const shownInSection = <T>(send: () => Promise<T>): Promise<T> => {
    isSending = true;
    try {
        return send();
    } finally {
        isSending = false;
    }
};

/**
 * Saves one section of a record whose update replaces it whole: the section's own fields as edited, over the
 * record as stored now. So the save never changes another section's field, even one the section's handlers set,
 * and never undoes a change saved since the section opened. A rejection propagates to `SectionEditForm`, which
 * keeps the section open and shows it there.
 */
export const saveSection = async <Dto, Form extends object, Request, Section extends string>(
    values: Form,
    {
        section,
        sectionFields,
        fetchLatest,
        toForm,
        toRequest,
        update,
        successMessageId,
        onSaved,
    }: SectionSaveOptions<Dto, Form, Request, Section>
): Promise<void> => {
    const latestDto = await shownInSection(fetchLatest);
    const latest = toForm(latestDto);
    const request = await toRequest(mergeSectionValues(latest, values, section, sectionFields), latest, latestDto);
    if (request === null) {
        return;
    }
    await shownInSection(() => update(request));
    if (successMessageId) {
        successAlert(t(successMessageId));
    }
    // The save went through; a failed refresh is the query's error, toasted as such, not the section's.
    await fetchLatest().catch(() => undefined);
    onSaved();
};
